import { useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import { createReadableClass } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { WAVEFORM_SAMPLES } from '../../../../constants';
import { useTheme } from '../../../common';
import { usePlayer } from '../../PlayerProvider';
import {
  generateMinimalPeaks,
  getAnalysisProgress,
  getDecompressedPeaks
} from './utils';
import { Wavesurfer } from './Wavesurfer';

interface WaveformProps {
  hideTime?: boolean;
}

export const Waveform = ({ hideTime = false }: WaveformProps) => {
  const { currentItem } = usePlayer();
  const { textColor } = useTheme();
  const {
    source,
    sourceType,
    accentColor,
    RSSMetadata = [],
    _globalPeaksSettings: globalPeaksSettings
  } = useSettings();

  const isRSS = sourceType === 'rss';
  const isWaveformSupported = sourceType === 'player' || sourceType === 'rss';

  const hasNoTracks = Array.isArray(source) && source.length === 0;

  const decompressedPeaks = useMemo(() => {
    if (hasNoTracks) {
      return generateMinimalPeaks(WAVEFORM_SAMPLES, 0);
    }

    return getDecompressedPeaks({
      currentItem,
      sourceType,
      globalPeaksSettings
    });
  }, [hasNoTracks, currentItem, sourceType, globalPeaksSettings]);

  const { completed, total, pending } = useMemo(
    () => getAnalysisProgress(isRSS ? RSSMetadata : source),
    [isRSS, RSSMetadata, source]
  );

  if (!isWaveformSupported) {
    console.warn(
      'Waveform analysis is not supported for the current source type.'
    );
    return null;
  }

  const isAnalyzing = pending > 0;

  if (isAnalyzing) {
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

    return (
      <AnalysisContainer>
        <AnalysisText $textColor={textColor}>
          Analyzing waveform. Please wait...
        </AnalysisText>
        <ProgressBarContainer $color={getColorWithAlpha(textColor, 0.3)}>
          <ProgressBarFill
            $percentage={percentage}
            $accentColor={accentColor}
          />
        </ProgressBarContainer>
        <ProgressText $textColor={textColor}>
          {completed} / {total} tracks
        </ProgressText>
      </AnalysisContainer>
    );
  }

  if (hasNoTracks && decompressedPeaks) {
    return (
      <Wavesurfer
        key="empty-waveform"
        decompressedPeaks={decompressedPeaks}
        hideTime={hideTime}
      />
    );
  }

  if (!currentItem || !decompressedPeaks) {
    return null;
  }

  return (
    <Wavesurfer
      key={currentItem.id}
      decompressedPeaks={decompressedPeaks}
      hideTime={hideTime}
    />
  );
};

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

const AnalysisContainer = styled.div.attrs({
  className: createReadableClass({
    component: 'waveform',
    element: 'analysis-container'
  })
})`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
`;

const AnalysisText = styled.div.attrs<{ $textColor: string }>({
  className: createReadableClass({
    component: 'waveform',
    element: 'analysis-text'
  })
})`
  font-size: 13px;
  font-weight: 500;
  color: ${({ $textColor }) => $textColor};
  animation: ${pulse} 2s ease-in-out infinite;
  text-align: center;
`;

const ProgressBarContainer = styled.div.attrs<{ $color: string }>({
  className: createReadableClass({
    component: 'waveform',
    element: 'progress-bar-container'
  })
})`
  width: 100%;
  height: 4px;
  background-color: ${({ $color }) => $color};
  border-radius: 2px;
  overflow: hidden;
  position: relative;
`;

const ProgressBarFill = styled.div.attrs<{
  $percentage: number;
  $accentColor: string;
}>({
  className: createReadableClass({
    component: 'waveform',
    element: 'progress-bar-fill'
  })
})`
  height: 100%;
  width: ${({ $percentage }) => $percentage}%;
  background-color: ${({ $accentColor }) => $accentColor};
  border-radius: 2px;
  transition: width 0.3s ease-out;
`;

const ProgressText = styled.div.attrs<{ $textColor: string }>({
  className: createReadableClass({
    component: 'waveform',
    element: 'progress-text'
  })
})`
  font-size: 11px;
  color: ${({ $textColor }) => $textColor};
  opacity: 0.7;
`;
